/*
 * `posthog`, loaded after the page (ALU-663).
 *
 * `posthog-js` is ~78 KB gzipped. Every client module used to import it
 * statically, which put it on the critical path of every page. They now import
 * this facade, which has the same shape for the calls we make and keeps the
 * SDK in its own chunk (`./posthog-boot`), fetched when the page has loaded
 * and the browser is idle (`schedulePostHogLoad`).
 *
 * Until the SDK is up:
 * - `capture`, `captureException`, `register`, `group` and
 *   `opt_out_capturing` are queued (bounded) and replayed in order the moment
 *   the SDK is ready, before the "SDK ready" handshake, so no event of the
 *   first seconds is lost.
 * - `__loaded` is false, exactly as before init, so every guard that waits
 *   for the SDK (identify, acquisition, mode switches) keeps waiting and runs
 *   again on `onAnalyticsSdkReady`.
 * - `__enabled` is true when the build has a key and nothing failed: the
 *   guard for "will accept events", used where queueing is right (`track`).
 *
 * Consent is preserved: a call queued in `full` mode is dropped if the person
 * went back to `anonymous` before the SDK arrived, and a call queued while
 * anonymous is sent without a person profile if they said yes in the
 * meantime (what was captured before consent stays unlinked). With no key the
 * SDK is never fetched and nothing is kept.
 */
import type { PostHog } from 'posthog-js'
import { getAnalyticsMode, type AnalyticsMode } from '@/lib/analytics/consent'
import { runAfterLoadIdle } from '@/lib/utils/after-load'

export type LazyPostHog = PostHog & { readonly __enabled: boolean }

/** Pending calls kept until the SDK is ready; the oldest go first when full. */
export const MAX_QUEUED_CALLS = 100

const QUEUED_METHODS = new Set([
  'capture',
  'captureException',
  'register',
  'group',
  'opt_out_capturing',
])
const CAPTURE_METHODS = new Set(['capture', 'captureException'])

interface QueuedCall {
  method: string
  args: unknown[]
  mode: AnalyticsMode
}

let real: PostHog | null = null
let loading: Promise<PostHog | null> | null = null
let failed = false
let queue: QueuedCall[] = []

function configured(): boolean {
  // Literal read so Next inlines it at build time.
  return !!process.env.NEXT_PUBLIC_POSTHOG_KEY
}

function enabled(): boolean {
  return typeof window !== 'undefined' && configured() && !failed
}

function enqueue(method: string, args: unknown[]): void {
  if (!enabled()) return
  if (queue.length >= MAX_QUEUED_CALLS) queue.shift()
  queue.push({ method, args, mode: getAnalyticsMode() })
}

function replayQueue(ph: PostHog): void {
  const now = getAnalyticsMode()
  const calls = queue
  queue = []
  for (const call of calls) {
    // Consent withdrawn while the SDK was on its way: send nothing of it.
    if (call.mode === 'full' && now === 'anonymous') continue
    let args = call.args
    if (call.mode === 'anonymous' && now === 'full' && CAPTURE_METHODS.has(call.method)) {
      // Captured before the yes: never tied to the person who says yes later.
      if (call.method === 'capture') {
        const [name, props, options] = args as [string, Record<string, unknown>?, unknown?]
        args = [name, { ...props, $process_person_profile: false }, options]
      } else {
        const [error, props] = args as [unknown, Record<string, unknown>?]
        args = [error, { ...props, $process_person_profile: false }]
      }
    }
    try {
      ;(ph[call.method as keyof PostHog] as (...a: unknown[]) => unknown)(...args)
    } catch {
      /* one bad call must not stop the rest */
    }
  }
}

/** Fetches and starts the SDK once; later calls share the same promise. */
export function loadPostHog(): Promise<PostHog | null> {
  if (!enabled()) return Promise.resolve(null)
  loading ??= import('./posthog-boot')
    .then(({ initPostHog, posthog }) => {
      real = posthog
      const started = initPostHog({ beforeReady: () => replayQueue(posthog) })
      if (!started || !posthog.__loaded) {
        failed = true
        real = null
        queue = []
        return null
      }
      return posthog
    })
    .catch(() => {
      // Chunk failed to load (offline, blocked, stale deploy): no analytics
      // this page load, and nothing kept in memory.
      failed = true
      real = null
      queue = []
      return null
    })
  return loading
}

/** Starts PostHog when the page has loaded and the browser is idle. */
export function schedulePostHogLoad(): void {
  if (!enabled()) return
  runAfterLoadIdle(() => void loadPostHog())
}

export const posthog = new Proxy({} as LazyPostHog, {
  get(_target, prop) {
    if (prop === '__enabled') return enabled()
    if (real?.__loaded) {
      const value = Reflect.get(real, prop)
      return typeof value === 'function' ? value.bind(real) : value
    }
    if (prop === '__loaded') return false
    if (prop === 'has_opted_out_capturing') return () => false
    if (typeof prop === 'string' && QUEUED_METHODS.has(prop)) {
      return (...args: unknown[]) => enqueue(prop, args)
    }
    return undefined
  },
})

export default posthog

/** Test seam: forget the loaded SDK and the queue. */
export function resetPostHogLazyForTests(): void {
  real = null
  loading = null
  failed = false
  queue = []
}
